import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const slices = [
  {
    time: '02:15',
    label: '导入',
    desc: '提问引入，现实场景切入',
    detail: '以现实问题引入教学主题',
    color: 'accent',
  },
  {
    time: '08:40',
    label: '案例进入',
    desc: '事实呈现，问题展开',
    detail: '呈现典型案例展开讨论',
    color: 'primary',
  },
  {
    time: '18:30',
    label: '提问',
    desc: '开放问题，学生回应',
    detail: '开放性提问激发思考',
    color: 'accent',
  },
  {
    time: '25:15',
    label: '互动',
    desc: '追问深入，回应进入理论',
    detail: '师生互动深入理论核心',
    color: 'primary',
  },
  {
    time: '38:00',
    label: '总结升华',
    desc: '价值回扣，教学收束',
    detail: '回扣价值完成教学闭环',
    color: 'accent',
  },
];

const colorClasses: Record<string, { dot: string; line: string; cardBorder: string; num: string; numBg: string; time: string; title: string }> = {
  primary: {
    dot: 'bg-primary-500',
    line: 'bg-primary-400',
    cardBorder: 'border-primary-200/60',
    num: 'text-primary-600',
    numBg: 'bg-primary-50',
    time: 'text-primary-600',
    title: 'text-primary-700',
  },
  accent: {
    dot: 'bg-accent-500',
    line: 'bg-accent-400',
    cardBorder: 'border-accent-200/60',
    num: 'text-accent-600',
    numBg: 'bg-accent-50',
    time: 'text-accent-600',
    title: 'text-accent-700',
  },
};

export default function P26ClassroomInputNew({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative min-h-screen flex flex-col overflow-y-auto">
      {/* 背景光效 */}
      <div ref={orb1Ref} className="absolute top-20 right-24 w-[380px] h-[380px] rounded-full bg-primary-200/30 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-[400px] h-[400px] rounded-full bg-accent-200/25 blur-[110px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] rounded-full bg-secondary-200/20 blur-[140px] pointer-events-none animate-orb-float-1" />

      <div className="relative z-10 w-full max-w-[1200px] mx-auto px-6 md:px-10 lg:px-16 py-8 md:py-12 pb-16">
        {/* 标题 */}
        <div ref={titleRef} className="text-center mb-4 md:mb-6">
          <span className={`inline-flex items-center gap-3 text-xl md:text-2xl font-body font-semibold text-accent-600 uppercase tracking-[0.2em] mb-3 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="w-8 h-px bg-accent-400" />
            <span className="gold-dot" />
            课堂实录与有效切片
            <span className="w-8 h-px bg-accent-400" />
          </span>
          <h2 className={`font-heading text-5xl md:text-6xl lg:text-7xl font-bold text-foreground-950 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            从长视频中<span className="bg-gradient-to-r from-primary-500 to-accent-500 bg-clip-text text-transparent">保留教学动作</span>
          </h2>
        </div>

        {/* 桌面端：上下交替时间线 */}
        <div className={`hidden md:block transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {/* 总时间 */}
          <div className="flex items-center justify-between mb-3 px-[60px]">
            <span className="text-lg font-mono text-foreground-950 tracking-wider">00:00</span>
            <span className="text-lg font-mono text-foreground-950 tracking-wider">45:00</span>
          </div>

          {/* 时间轴区域 */}
          <div className="relative">
            {/* 主线 */}
            <div className="absolute left-[60px] right-[60px] top-[120px] h-[2px] bg-gradient-to-r from-secondary-200 via-primary-300 to-accent-300 opacity-40" />

            {/* 5个节点 */}
            <div className="flex justify-between px-[60px] relative">
              {slices.map((slice, idx) => {
                const cc = colorClasses[slice.color];
                const isTop = idx % 2 === 0;

                return (
                  <div key={slice.time} className="relative flex flex-col items-center" style={{ width: '160px' }}>
                    {/* 上方卡片（奇数） */}
                    {isTop && (
                      <>
                        <div
                          className={`w-full glass-liquid rounded-xl border ${cc.cardBorder} p-4 md:p-5 transition-all duration-700 hover:-translate-y-1 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                          style={{ transitionDelay: `${400 + idx * 120}ms` }}
                        >
                          <div className="flex items-center gap-2 mb-1.5">
                            <span className={`inline-flex w-9 h-9 rounded-md ${cc.numBg} items-center justify-center text-lg font-bold font-label ${cc.num}`}>
                              {idx + 1}
                            </span>
                            <span className={`text-lg font-mono font-semibold ${cc.time}`}>{slice.time}</span>
                          </div>
                          <h4 className={`text-2xl font-bold font-heading ${cc.title} mb-1.5`}>{slice.label}</h4>
                          <p className="text-lg text-foreground-950 font-body leading-relaxed">{slice.desc}</p>
                        </div>
                        {/* 连接线 */}
                        <div className={`w-[2px] h-4 md:h-5 ${cc.line} opacity-40 mt-1`} />
                      </>
                    )}

                    {/* 节点圆点 */}
                    <div
                      className={`relative z-10 w-6 h-6 rounded-full ${cc.dot} ring-4 ring-background-50 shadow-sm transition-all duration-700 ${titleVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-0'}`}
                      style={{ transitionDelay: `${500 + idx * 100}ms` }}
                    >
                      <div className="absolute inset-1 rounded-full bg-background-50" />
                    </div>

                    {/* 下方卡片（偶数） */}
                    {!isTop && (
                      <>
                        {/* 连接线 */}
                        <div className={`w-[2px] h-4 md:h-5 ${cc.line} opacity-40 mb-1`} />
                        <div
                          className={`w-full glass-liquid rounded-xl border ${cc.cardBorder} p-4 md:p-5 transition-all duration-700 hover:-translate-y-1 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                          style={{ transitionDelay: `${400 + idx * 120}ms` }}
                        >
                          <div className="flex items-center gap-2 mb-1.5">
                            <span className={`inline-flex w-9 h-9 rounded-md ${cc.numBg} items-center justify-center text-lg font-bold font-label ${cc.num}`}>
                              {idx + 1}
                            </span>
                            <span className={`text-lg font-mono font-semibold ${cc.time}`}>{slice.time}</span>
                          </div>
                          <h4 className={`text-2xl font-bold font-heading ${cc.title} mb-1.5`}>{slice.label}</h4>
                          <p className="text-lg text-foreground-950 font-body leading-relaxed">{slice.desc}</p>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* 移动端：竖向时间线 */}
        <div className={`md:hidden transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="relative pl-2">
            {/* 主线 */}
            <div className="absolute left-[19px] top-4 bottom-4 w-[2px] bg-gradient-to-b from-secondary-200 via-primary-300 to-accent-300 opacity-40" />

            {slices.map((slice, idx) => {
              const cc = colorClasses[slice.color];
              return (
                <div
                  key={slice.time}
                  className={`relative flex items-start gap-4 mb-4 last:mb-0 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                  style={{ transitionDelay: `${400 + idx * 120}ms` }}
                >
                  {/* 节点 */}
                  <div className="relative z-10 flex-shrink-0 mt-3">
                    <div className={`w-5 h-5 rounded-full ${cc.dot} ring-4 ring-background-50 shadow-sm flex items-center justify-center`}>
                      <div className="w-1.5 h-1.5 rounded-full bg-background-50" />
                    </div>
                  </div>

                  {/* 卡片 */}
                  <div className={`flex-1 glass-liquid rounded-xl border ${cc.cardBorder} p-4`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`inline-flex w-9 h-9 rounded-md ${cc.numBg} items-center justify-center text-lg font-bold font-label ${cc.num}`}>
                        {idx + 1}
                      </span>
                      <span className={`text-lg font-mono font-semibold ${cc.time}`}>{slice.time}</span>
                    </div>
                    <h4 className={`text-2xl font-bold font-heading ${cc.title} mb-1.5`}>{slice.label}</h4>
                    <p className="text-xl text-foreground-950 font-body leading-relaxed">{slice.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* 技术底座 */}
        <div className={`mt-6 md:mt-8 transition-all duration-1000 delay-500 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="rounded-2xl bg-background-100/70 backdrop-blur-sm border border-background-200/60 p-4 md:p-5 flex flex-col sm:flex-row items-center gap-4 md:gap-5 max-w-2xl mx-auto">
            <div className="w-12 h-12 rounded-xl glass-gold border-accent-200/40 flex items-center justify-center flex-shrink-0">
              <i className="ri-sparkling-line text-accent-500 text-xl" />
            </div>
            <div className="flex-1 min-w-0 text-center sm:text-left">
              <div className="flex items-center gap-2 mb-2 flex-wrap justify-center sm:justify-start">
                <span className="inline-block px-4 py-1.5 rounded-full glass-gold text-accent-700 text-lg font-bold font-label tracking-wider border border-accent-200/30">
                  自研技术
                </span>
                <span className="text-lg text-accent-600 font-body">驱动切片过程</span>
              </div>
              <p className="text-3xl font-bold text-foreground-950 font-heading">
                核心关键帧采样算法
              </p>
              <p className="text-lg text-foreground-950 font-body mt-1">
                自动识别课堂关键转折点，精准提取有效教学片段
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}